/**
 * Calculate canvas-size relative thresholds for consistent snapping experience
 * across different canvas sizes
 */

/**
 * Calculate scaling factor based on canvas dimensions
 * @param {fabric.Canvas} canvas - The fabric canvas
 * @returns {number} Scaling factor for thresholds
 */
const calculateScaleFactor = (canvas) => {
  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  // Use the smaller dimension to ensure snapping works well in both directions
  const minDimension = Math.min(canvasWidth, canvasHeight);

  // Base reference size (1000px)
  // Canvases smaller than this will have reduced thresholds
  // Canvases larger than this will have increased thresholds
  const referenceSize = 1000;

  // Calculate scale factor with bounds
  // Min: 0.5x (for very small canvases)
  // Max: 3x (for very large canvases)
  const scaleFactor = Math.max(0.5, Math.min(3, minDimension / referenceSize));

  return scaleFactor;
};

/**
 * Calculate relative thresholds based on canvas size
 * @param {fabric.Canvas} canvas - The fabric canvas
 * @param {Object} baseThresholds - Base threshold values from config
 * @returns {Object} Scaled threshold values
 */
export const calculateRelativeThresholds = (canvas, baseThresholds) => {
  const scaleFactor = calculateScaleFactor(canvas);

  // Apply scaling to all threshold values
  const scaledThresholds = {};

  for (const [key, value] of Object.entries(baseThresholds)) {
    if (typeof value === 'number' && key.includes('Threshold')) {
      // Round to nearest pixel for cleaner snapping
      scaledThresholds[key] = Math.round(value * scaleFactor);
    } else {
      // Keep non-threshold values unchanged
      scaledThresholds[key] = value;
    }
  }

  // Also scale alignment tolerance if present
  if (typeof baseThresholds.alignmentTolerance === 'number') {
    scaledThresholds.alignmentTolerance = Math.round(
      baseThresholds.alignmentTolerance * scaleFactor
    );
  }

  return scaledThresholds;
};

/**
 * Get canvas size category for debugging/logging
 * @param {fabric.Canvas} canvas - The fabric canvas
 * @returns {string} Size category (small/medium/large)
 */
export const getCanvasSizeCategory = (canvas) => {
  const minDimension = Math.min(canvas.getWidth(), canvas.getHeight());

  if (minDimension < 500) return 'small';
  if (minDimension < 1500) return 'medium';
  return 'large';
};
